import { BrandCategory } from './brands';
import { getModelAvailableSides } from './models';

export const DESIGN_SCHEMA_VERSION = 1;

const CATEGORY_TO_CODE = {
  [BrandCategory.SHAPES_SYMBOLS]: 's',
  [BrandCategory.ALPHANUMERIC_LARGE]: 'l',
  [BrandCategory.ALPHANUMERIC_SMALL]: 'h',
  [BrandCategory.GREEK_ALPHABET]: 'g',
};

const CODE_TO_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_TO_CODE).map(([category, code]) => [code, category])
);

const round = (value, precision) => {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }

  const multiplier = 10 ** precision;
  return Math.round(number * multiplier) / multiplier;
};

const encodeBase64Url = (value) => {
  const binary = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_match, hex) => String.fromCharCode(parseInt(hex, 16))
  );

  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const decodeBase64Url = (value) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = window.atob(normalized + padding);
  const encoded = Array.from(binary, (character) =>
    `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
  ).join('');
  return decodeURIComponent(encoded);
};

export const serializeDesign = (canvases, model) => {
  const sides = {};

  getModelAvailableSides(model).forEach((side) => {
    const objects = canvases?.[side]?.getObjects?.() ?? [];
    if (!objects.length) {
      return;
    }

    sides[side] = objects
      .filter((object) => object?.name)
      .map((object) => [
        object.name,
        CATEGORY_TO_CODE[object.brandCategory] ?? 's',
        round(object.left, 2),
        round(object.top, 2),
        round(object.scaleX, 5),
        round(object.scaleY ?? object.scaleX, 5),
        round(object.angle, 2),
      ]);
  });

  return {
    v: DESIGN_SCHEMA_VERSION,
    m: model.name,
    s: sides,
  };
};

export const hasDesignObjects = (design) =>
  Object.values(design?.s ?? {}).some((entries) => entries.length > 0);

export const encodeDesignToken = (design) => encodeBase64Url(JSON.stringify(design));

export const decodeDesignToken = (token) => {
  if (!token) {
    return null;
  }

  try {
    const design = JSON.parse(decodeBase64Url(token));
    if (
      design?.v !== DESIGN_SCHEMA_VERSION ||
      typeof design.m !== 'string' ||
      !design.s ||
      typeof design.s !== 'object'
    ) {
      return null;
    }

    return design;
  } catch (_error) {
    return null;
  }
};

export const getDesignEntriesForSide = (design, side) => {
  const entries = design?.s?.[side];
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries.flatMap((entry) => {
    if (
      !Array.isArray(entry) ||
      (entry.length !== 6 && entry.length !== 7) ||
      typeof entry[0] !== 'string'
    ) {
      return [];
    }

    const [, categoryCode, left, top, scaleX] = entry;
    const scaleY = entry.length === 7 ? entry[5] : scaleX;
    const angle = entry.length === 7 ? entry[6] : entry[5];
    const numericValues = [left, top, scaleX, scaleY, angle].map(Number);
    if (!numericValues.every(Number.isFinite)) {
      return [];
    }

    return [{
      assetName: entry[0],
      category: CODE_TO_CATEGORY[categoryCode] ?? BrandCategory.SHAPES_SYMBOLS,
      left: numericValues[0],
      top: numericValues[1],
      scaleX: numericValues[2],
      scaleY: numericValues[3],
      angle: numericValues[4],
    }];
  });
};

export const buildReviewUrl = ({ designToken, modelName, currentUrl = window.location.href }) => {
  const reviewUrl = new URL(currentUrl);
  reviewUrl.search = '';
  reviewUrl.hash = '';
  reviewUrl.searchParams.set('model', modelName);
  reviewUrl.searchParams.set('design', designToken);
  reviewUrl.searchParams.set('review', '1');
  return reviewUrl.toString();
};
